import React, { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Fuse from "fuse.js";

import {
  useGetVendorProductsQuery,
  useGetVendorCategorySubcategoryMapQuery,
} from "../slices/productApiSlice";
import { useCreateOrderMutation } from "../slices/orderApiSlice";

const SearchSalesOrderScreen = () => {
  const navigate = useNavigate();
  const { userInfo } = useSelector((state) => state.auth);

  const { data: products = [] } = useGetVendorProductsQuery();
  const {
    data: categoryMapData,
    error,
    isSuccess,
    isError,
  } = useGetVendorCategorySubcategoryMapQuery();

  const [createOrder] = useCreateOrderMutation();
  const [salesOrder, setSalesOrder] = useState(null);

  useEffect(() => {
    if (isError) console.error("Error fetching category map:", error);
    if (isSuccess) console.log("Category map received:", categoryMapData);
  }, [isError, isSuccess]);

  const structuredProductMap = useMemo(() => {
    const map = {};
    for (const product of products) {
      const { category, subcategory } = product;
      if (!map[category]) map[category] = {};
      if (!map[category][subcategory]) map[category][subcategory] = [];
      map[category][subcategory].push(product);
    }
    return map;
  }, [products]);

  useEffect(() => {
    if (userInfo?._id) {
      const defaultAddress =
        userInfo.addresses?.[0] || {
          address: "",
          city: "",
          pincode: "",
          phoneNumber: "",
        };

      setSalesOrder({
        shippingAddress: { ...defaultAddress },
        products: [
          {
            product: "",
            vendorId: "",
            category: "",
            subCategory: "",
            quantity: 1,
            ProductTotalprice: 0,
            discountApplied: 0,
            discountAmount: 0,
          },
        ],
        OrderTotalprice: 0,
      });
    }
  }, [userInfo]);

  // Update order total whenever products change
  useEffect(() => {
    if (!salesOrder) return;
    const total = salesOrder.products.reduce(
      (acc, p) => acc + (p.ProductTotalprice || 0),
      0
    );
    setSalesOrder((prev) => ({ ...prev, OrderTotalprice: total }));
  }, [salesOrder?.products]);

  const handleProductChange = (index, field, value) => {
    if (!salesOrder) return;

    const updatedProducts = [...salesOrder.products];
    const currentItem = updatedProducts[index];

    if (field === "product") {
      const selectedProduct = products.find((p) => p._id === value);
      if (selectedProduct) {
        const discount = selectedProduct.discount || 0;
        const price = selectedProduct.shopPrice || selectedProduct.price || 0;
        const finalUnitPrice = Math.max(price - discount, 0);

        updatedProducts[index] = {
          ...currentItem,
          product: selectedProduct._id,
          vendorId: selectedProduct.vendorId,
          category: selectedProduct.category,
          subCategory: selectedProduct.subcategory,
          quantity: 1,
          discountApplied: discount,
          ProductTotalprice: finalUnitPrice,
          discountAmount: discount,
        };
      }
    } else if (field === "quantity") {
      const quantity = Math.max(Number(value) || 1, 1);
      const selectedProduct = products.find(
        (p) => p._id === currentItem.product
      );
      if (!selectedProduct) return;

      const discount = selectedProduct.discount || 0;
      const price = selectedProduct.shopPrice || selectedProduct.price || 0;
      const finalUnitPrice = Math.max(price - discount, 0);

      updatedProducts[index] = {
        ...currentItem,
        quantity,
        ProductTotalprice: finalUnitPrice * quantity,
        discountAmount: discount * quantity,
      };
    }

    setSalesOrder({ ...salesOrder, products: updatedProducts });
  };

  const addProductLine = () => {
    setSalesOrder((prev) => ({
      ...prev,
      products: [
        ...prev.products,
        {
          product: "",
          vendorId: "",
          category: "",
          subCategory: "",
          quantity: 1,
          ProductTotalprice: 0,
          discountApplied: 0,
          discountAmount: 0,
        },
      ],
    }));
  };

  const removeProductLine = (index) => {
    const updated = [...salesOrder.products];
    updated.splice(index, 1);
    setSalesOrder({ ...salesOrder, products: updated });
  };

  const isDuplicateProduct = (products, idx) => {
    const current = products[idx].product;
    return current && products.filter((p) => p.product === current).length > 1;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const address = salesOrder.shippingAddress;
    if (
      !address.address ||
      !address.city ||
      !address.pincode ||
      !address.phoneNumber
    ) {
      toast.error("Please fill in complete shipping address");
      return;
    }

    if (
      salesOrder.products.some((_, idx) =>
        isDuplicateProduct(salesOrder.products, idx)
      )
    ) {
      toast.error("Duplicate products detected.");
      return;
    }

    for (const p of salesOrder.products) {
      if (!p.product || p.quantity <= 0) {
        toast.error("Please select valid products and quantity");
        return;
      }
    }

    // Format payload for backend
    const formattedProducts = salesOrder.products.map((p, index) => {
  const prod = products.find((x) => x._id === p.product);

  if (!prod) {
    throw new Error(`Invalid product at row ${index + 1}`);
  }

  const quantity = Math.max(Number(p.quantity) || 1, 1);
  const discountPerUnit = Number(prod.discount) || 0;

  return {
    vendorId: userInfo._id,   // ✅ ALWAYS vendor = logged-in user
    productId: prod._id,
    quantity,
    discount: discountPerUnit * quantity,
  };
});

console.log("ORDER PAYLOAD", formattedProducts);


    try {
      await createOrder({
        products: formattedProducts,
        shippingAddress: salesOrder.shippingAddress,
        paymentStatus: "Paid",
        isShop: true, // ✅ must be true for vendor orders
      }).unwrap();

      toast.success("Order created successfully!");
      navigate("/PendingOrders");
    } catch (err) {
      console.error("Order creation failed:", err);
      toast.error(err?.data?.message || "Order creation failed");
    }
  };

  if (!salesOrder) return <div>Loading...</div>;

  return (
    <div className="container my-4">
      <h2 className="text-center mb-4">Create Order</h2>

      <form
        onSubmit={handleSubmit}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.preventDefault();
        }}
      >
        <div className="card shadow-sm mb-4">
          <div className="card-header bg-success text-white">
            <h5 className="mb-0">Products</h5>
          </div>

          <div className="card-body">
            {salesOrder.products.map((item, idx) => (
              <ProductLine
                key={idx}
                idx={idx}
                item={item}
                handleProductChange={handleProductChange}
                removeProductLine={removeProductLine}
                isDuplicate={isDuplicateProduct(salesOrder.products, idx)}
                products={products}
              />
            ))}

            <button
              type="button"
              className="btn btn-outline-success btn-sm"
              onClick={addProductLine}
            >
              + Add Product
            </button>
          </div>
        </div>

        <div className="text-end mb-4">
          <h4>
            Order Total Price:{" "}
            <span className="text-success">
              ₹{salesOrder.OrderTotalprice.toFixed(2)}
            </span>
          </h4>
        </div>

        <div className="text-center">
          <button type="submit" className="btn btn-primary">
            Submit Order
          </button>
        </div>
      </form>
    </div>
  );
};

export default SearchSalesOrderScreen;

/* ----------------------------
   ProductSearch Component
---------------------------- */
const ProductSearch = ({ products, onSelectProduct }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [filtered, setFiltered] = useState([]);

  const fuse = useMemo(
    () =>
      new Fuse(products, {
        keys: ["name", "category", "subcategory"],
        threshold: 0.4,
        includeScore: true,
      }),
    [products]
  );

  useEffect(() => {
    if (!searchTerm.trim()) {
      setFiltered([]);
      return;
    }
    const results = fuse.search(searchTerm);
    setFiltered(results.map((r) => r.item).slice(0, 10));
  }, [searchTerm, fuse]);

  const handleSelect = (product) => {
    onSelectProduct(product);
    setSearchTerm("");
    setFiltered([]);
  };

  return (
    <div className="position-relative">
      <input
        type="text"
        className="form-control"
        placeholder="Search product..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
      />

      {filtered.length > 0 && (
        <ul
          className="list-group position-absolute w-100"
          style={{ zIndex: 1000, maxHeight: "220px", overflowY: "auto" }}
        >
          {filtered.map((prod) => (
            <li
              key={prod._id}
              className="list-group-item list-group-item-action"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => handleSelect(prod)}
              style={{ cursor: "pointer" }}
            >
              <strong>{prod.name}</strong>
              <div className="text-muted small">
                {prod.category} → {prod.subcategory}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

/* ----------------------------
   ProductLine Component
---------------------------- */
const ProductLine = ({
  idx,
  item,
  handleProductChange,
  removeProductLine,
  isDuplicate,
  products,
}) => {
  const selectedProduct = products.find((p) => p._id === item.product);

  const increment = () => {
    handleProductChange(idx, "quantity", item.quantity + 1);
  };

  const decrement = () => {
    handleProductChange(idx, "quantity", Math.max(item.quantity - 1, 1));
  };

  return (
    <div className="row align-items-end mb-3 p-2 border rounded">
      <div className="col-md-3">
        <label className="form-label">Search Product</label>
        <ProductSearch
          products={products}
          onSelectProduct={(p) => handleProductChange(idx, "product", p._id)}
        />
        {selectedProduct && (
          <div className="mt-2">
            <span className="badge bg-secondary">{selectedProduct.name}</span>
          </div>
        )}
      </div>

      <div className="col-md-2">
        <label className="form-label">Category</label>
        <input type="text" className="form-control" value={item.category} readOnly />
      </div>

      <div className="col-md-2">
        <label className="form-label">Subcategory</label>
        <input type="text" className="form-control" value={item.subCategory} readOnly />
      </div>

      <div className="col-md-2">
        <label className="form-label">Quantity</label>

        <div className="d-flex align-items-center">
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={decrement}
            disabled={!item.product}
          >
            −
          </button>

          <div
            className="mx-2 px-3 py-1 border rounded text-center"
            style={{ minWidth: "40px", background: "#f8f9fa" }}
          >
            {item.quantity}
          </div>

          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={increment}
            disabled={!item.product}
          >
            +
          </button>
        </div>

        <small className="text-muted d-block mt-1">
          Discount: ₹{item.discountApplied}
        </small>
      </div>

      <div className="col-md-2">
        <p className="mb-0 mt-4">
          <strong>Total: ₹{item.ProductTotalprice.toFixed(2)}</strong>
        </p>
      </div>

      <div className="col-md-1 text-end">
        <button
          type="button"
          className="btn btn-danger btn-sm mt-4"
          onClick={() => removeProductLine(idx)}
        >
          &times;
        </button>
      </div>
    </div>
  );
};
